import { useState, type KeyboardEvent } from 'react'
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/primitives'
import { DuplicateCategoryError, type TodoCategory } from '@/shared/domain'
import { cn } from '@/shared/ui/cn'

const SWATCHES = [
  'var(--color-accent-danger)',
  'var(--color-accent-brand)',
  'var(--color-accent-success)',
  'var(--color-accent-info)',
  'var(--color-accent-purple)',
  '#EC4899',
  '#14B8A6',
  '#F97316',
] as const

export type CategorySelectProps = {
  categories: TodoCategory[]
  selectedCategoryId: string | null
  onSelect: (id: string | null) => void
  onCreateCategory: (name: string, color: string) => Promise<TodoCategory | null>
}

export function CategorySelect({
  categories,
  selectedCategoryId,
  onSelect,
  onCreateCategory,
}: CategorySelectProps) {
  const [isCreating, setIsCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState<string>(SWATCHES[0])
  const [isSaving, setIsSaving] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId) ?? null

  const openEditor = () => {
    setIsCreating(true)
    setNewName('')
    setNewColor(SWATCHES[0])
    setCreateError(null)
  }

  const closeEditor = () => {
    setIsCreating(false)
    setCreateError(null)
    setNewName('')
    setNewColor(SWATCHES[0])
  }

  const handleCreate = async () => {
    if (!newName.trim()) return
    setIsSaving(true)
    setCreateError(null)
    try {
      const result = await onCreateCategory(newName.trim(), newColor)
      if (result) {
        onSelect(result.id)
        closeEditor()
      }
    } catch (err) {
      if (err instanceof DuplicateCategoryError) {
        setCreateError('Category already exists')
      } else {
        throw err
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation()
    if (event.key === 'Enter') {
      event.preventDefault()
      void handleCreate()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      closeEditor()
    }
  }

  return (
    <Select
      value={selectedCategoryId ?? ''}
      onValueChange={(value) => onSelect(value || null)}
    >
      <SelectTrigger>
        <SelectValue placeholder="Select category">
          {selectedCategory ? (
            <span className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block size-2.5 rounded-full"
                style={{ backgroundColor: selectedCategory.color }}
              />
              {selectedCategory.name}
            </span>
          ) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {categories.map((category) => (
          <SelectItem key={category.id} value={category.id}>
            <span className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block size-2.5 rounded-full"
                style={{ backgroundColor: category.color }}
              />
              {category.name}
            </span>
          </SelectItem>
        ))}
        <SelectSeparator />
        {isCreating ? (
          <div className="flex flex-col gap-2 p-2">
            <Input
              autoFocus
              maxLength={50}
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Category name"
              aria-label="Category name"
            />
            <div className="flex flex-wrap gap-2" role="group" aria-label="Category color">
              {SWATCHES.map((swatch) => {
                const selected = swatch === newColor
                return (
                  <button
                    key={swatch}
                    type="button"
                    aria-label={`Color ${swatch}`}
                    aria-pressed={selected}
                    onClick={() => setNewColor(swatch)}
                    className={cn(
                      'size-6 rounded-full transition-transform',
                      selected && 'ring-2 ring-offset-1 scale-110',
                    )}
                    style={{ backgroundColor: swatch }}
                  />
                )
              })}
            </div>
            {createError ? (
              <p role="alert" className="text-sm text-destructive">
                {createError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={closeEditor}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={isSaving}
                onClick={() => {
                  void handleCreate()
                }}
              >
                Create
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={openEditor}
            className="relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
          >
            + Add new category
          </button>
        )}
      </SelectContent>
    </Select>
  )
}
